<!-- static content data from lang: glp1_history -->
<template>
    <div class="container h-100">
        <div class="py-3 h-100" v-loading="loading">
            <b-card class="script-details">
                <template slot="header">
                    <!-- Equal-width sides keep the title centred -->
                    <div class="d-flex align-items-center">
                        <div class="glp1-header-side">
                            <b-btn variant="outline-light" size="sm" class="glp1-back" @click="$router.back()">
                                <i class="fas fa-arrow-left mr-2"></i>{{lang.label.buttons.back}}
                            </b-btn>
                        </div>
                        <h5 class="id mb-0 text-center flex-grow-1">{{lang.label.header}}</h5>
                        <div class="glp1-header-side"></div>
                    </div>
                </template>

                <p v-if="errored" class="text-danger text-center my-3">{{lang.label.error}}</p>
                <template v-else-if="history">
                    <!-- One column per visit: Start (initial application), Follow-up 1, Follow-up 2... -->
                    <h5 class="glp1-section-title">
                        <i class="fas fa-chart-line mr-2"></i>{{lang.label.comparison}}
                    </h5>
                    <div class="table-responsive mb-4">
                        <table class="table table-bordered glp1-comparison mb-0">
                            <thead class="thead-light">
                            <tr>
                                <th>{{lang.label.measure}}</th>
                                <th v-for="(column, index) in history.comparison.columns" :key="index"
                                    :class="{'glp1-current': isCurrentColumn(index)}">
                                    {{column.label}}
                                    <b-badge v-if="isCurrentColumn(index)" variant="primary" class="ml-1">{{lang.label.thisRequest}}</b-badge>
                                    <small class="d-block text-muted">{{column.date}}</small>
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="row in history.comparison.rows" :key="row.label">
                                <th>{{row.label}}</th>
                                <td v-for="(value, index) in row.values" :key="index"
                                    :class="{'glp1-current': isCurrentColumn(index), 'glp1-wrap': value.length > 20}">
                                    {{value !== '' ? value : '-'}}
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>

                    <p v-if="!history.application" class="text-muted">{{lang.label.noApplication}}</p>
                    <p v-if="!history.followUps.length" class="text-muted">{{lang.label.noFollowUps}}</p>

                    <!-- Full form of each visit; the request the doctor opened starts expanded -->
                    <h5 class="glp1-section-title">
                        <i class="fas fa-file-medical mr-2"></i>{{lang.label.visits}}
                    </h5>
                    <b-card v-for="visit in visits" :key="visit.formsManagementId" no-body
                            class="glp1-visit mb-2" :class="{'glp1-visit-current': visit.isCurrent}">
                        <button type="button" class="glp1-visit-toggle" v-b-toggle="'glp1-visit-' + visit.formsManagementId">
                            <span>
                                <i class="fas fa-chevron-down glp1-chevron mr-2"></i>
                                <b>{{visit.label}}</b>
                                <span v-if="visit === history.application" class="text-muted"> - {{lang.label.initialApplication}}</span>
                                <b-badge v-if="visit.isCurrent" variant="primary" class="ml-2">{{lang.label.thisRequest}}</b-badge>
                            </span>
                            <span class="text-muted">{{visit.date}}</span>
                        </button>
                        <b-collapse :id="'glp1-visit-' + visit.formsManagementId" :visible="visit.isCurrent">
                            <b-card-body class="pt-0">
                                <div class="row glp1-detail-row" v-for="(fdata, index) in visit.formDetails" :key="index">
                                    <div class="col-6">
                                        <span class="my-2 d-block">{{fdata.key}}</span>
                                    </div>
                                    <div class="col-6">
                                        <!-- Signatures come as image links (type: image) -->
                                        <a v-if="fdata.type === 'image'" :href="fdata.value" target="_blank" rel="noopener" class="my-2 d-block">
                                            <img :src="fdata.value" :alt="fdata.key" class="form-detail-image">
                                        </a>
                                        <span v-else class="my-2 d-block" :class="{ 'form-detail-yes': isYesAnswer(fdata) }">{{fdata.value !== '' ? fdata.value : lang.label.nodata}}</span>
                                    </div>
                                </div>
                            </b-card-body>
                        </b-collapse>
                    </b-card>
                </template>
            </b-card>
        </div>
    </div>
</template>

<script>
	export default {
		props: ['formsManagementId'],
		data() {
			return {
				lang: this.$lang.glp1_history,
				history: null,
				loading: true,
				errored: false
			}
		},
		computed: {
			// Visits in date order: the initial application first, then the follow-ups
			visits() {
				return (this.history.application ? [this.history.application] : []).concat(this.history.followUps);
			}
		},
		mounted() {
			this.$store.dispatch('getGlp1History', {formsManagementId: this.formsManagementId}).then(value => {
				this.loading = false;
				if (value && value.comparison) {
					this.history = value;
				} else {
					this.errored = true;
				}
			});
		},
		methods: {
			// API flags answers to highlight with isAnswer: "yes"
			isYesAnswer(fdata) {
				return String(fdata.isAnswer).toLowerCase() === 'yes';
			},
			// Comparison columns follow the visits, so the opened request is always the last column
			isCurrentColumn(index) {
				return index === this.history.comparison.columns.length - 1;
			}
		}
	};
</script>

<style scoped>
    .glp1-header-side {
        flex: 0 0 180px;
    }

    .glp1-back {
        min-height: 0;
        min-width: 0;
        color: #fff;
    }

    .glp1-section-title {
        font-size: 1.1rem;
        font-weight: 600;
        margin: 8px 0 12px;
    }

    .glp1-comparison th, .glp1-comparison td {
        white-space: nowrap;
        font-size: 13px;
        vertical-align: middle;
    }

    .glp1-comparison tbody th {
        background-color: #f8f9fa;
    }

    .glp1-comparison td.glp1-wrap {
        white-space: normal;
        min-width: 160px;
    }

    .glp1-comparison .glp1-current {
        background-color: #eef4ff;
    }

    .glp1-visit {
        border: 1px solid #dee2e6;
        overflow: hidden;
    }

    .glp1-visit-current {
        border-color: #007bff;
    }

    .glp1-visit-toggle {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        padding: 12px 16px;
        border: 0;
        background: #f8f9fa;
        text-align: left;
    }

    .glp1-visit-toggle:focus {
        outline: none;
    }

    .glp1-chevron {
        transition: transform .2s;
    }

    .glp1-visit-toggle.collapsed .glp1-chevron {
        transform: rotate(-90deg);
    }

    .glp1-detail-row {
        border-bottom: 1px solid #f1f1f1;
    }

    .glp1-detail-row:last-child {
        border-bottom: 0;
    }

    @media (max-width: 576px) {
        .glp1-header-side {
            flex-basis: auto;
        }

        .glp1-header-side:last-child {
            display: none;
        }
    }
</style>
